import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Send, ArrowLeft, User, Shield, CheckCircle2 } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { useTranslation } from 'react-i18next';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const SupportTicketChat = () => {
  const { ticketId } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t } = useTranslation();
  const messagesEndRef = useRef(null);
  
  const [loading, setLoading] = useState(true);
  const [ticket, setTicket] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    loadTicket();
    loadMessages();
    
    const interval = setInterval(() => {
      loadMessages(true);
    }, 10000);
    
    return () => clearInterval(interval);
  }, [ticketId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadTicket = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.get(`${API}/support/tickets/${ticketId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setTicket(response.data);
    } catch (error) {
      toast({
        title: t('common.error'),
        description: t('supportTicket.loadFailed'),
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const loadMessages = async (silent = false) => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.get(`${API}/support/tickets/${ticketId}/messages`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setMessages(response.data);
    } catch (error) {
      if (!silent) {
        console.error('Failed to load messages:', error);
      }
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    
    if (!newMessage.trim()) return;

    setSending(true);
    try {
      const token = localStorage.getItem('accessToken');
      await axios.post(
        `${API}/support/tickets/${ticketId}/messages`,
        {
          ticketId: ticketId,
          message: newMessage,
          attachments: []
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setNewMessage('');
      await loadMessages();
      
      toast({
        title: t('supportTicket.messageSentTitle'),
        description: t('supportTicket.messageSentDescription')
      });
    } catch (error) {
      toast({
        title: t('common.error'),
        description: t('supportTicket.sendFailed'),
        variant: 'destructive'
      });
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-2 border-gray-200 border-t-[#be1f1a]"></div>
      </div>
    );
  }

  if (!ticket) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p>{t('supportTicket.notFound')}</p>
      </div>
    );
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'open': return 'bg-yellow-100 text-yellow-800';
      case 'in_progress': return 'bg-blue-100 text-blue-800';
      case 'resolved': return 'bg-green-100 text-green-800';
      case 'closed': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate(-1)}
                className="text-gray-600 hover:text-[#be1f1a]"
              >
                <ArrowLeft className="h-5 w-5" />
              </button>
              <div>
                <h1 className="text-xl font-bold text-[#2C2420]">{ticket.subject}</h1>
                <p className="text-sm text-gray-600">{t('checkoutSuccess.orderNumberLabel', { n: ticket.orderId })}</p>
              </div>
            </div>
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(ticket.status)}`}>
              {t(`supportTickets.status.${ticket.status}`, { defaultValue: ticket.status.replace('_', ' ').toUpperCase() })}
            </span>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-6 max-w-4xl">
        <div className="bg-white rounded-lg shadow-sm">
          <div className="h-[500px] overflow-y-auto p-6 space-y-4">
            {messages.map((msg) => (
              <div
                key={msg.id}
                className={`flex gap-3 ${msg.senderType === 'customer' ? 'flex-row-reverse' : ''}`}
              >
                <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
                  msg.senderType === 'admin' ? 'bg-[#be1f1a] text-white' :
                  msg.senderType === 'system' ? 'bg-gray-200 text-gray-600' :
                  'bg-blue-500 text-white'
                }`}>
                  {msg.senderType === 'admin' ? <Shield className="h-4 w-4" /> :
                   msg.senderType === 'system' ? <CheckCircle2 className="h-4 w-4" /> :
                   <User className="h-4 w-4" />}
                </div>
                <div className={`flex-1 max-w-[70%] ${msg.senderType === 'customer' ? 'items-end' : ''}`}>
                  <div className={`rounded-lg p-4 ${
                    msg.senderType === 'admin' ? 'bg-[#FAF8F5] border border-[#be1f1a]/20' :
                    msg.senderType === 'system' ? 'bg-gray-100 text-gray-700 italic' :
                    'bg-blue-500 text-white'
                  }`}>
                    <p className="text-sm font-medium mb-1">{msg.senderName}</p>
                    <p className="text-sm">{msg.message}</p>
                  </div>
                  <p className="text-xs text-gray-500 mt-1 px-2">
                    {new Date(msg.createdAt).toLocaleString()}
                  </p>
                </div>
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>

          {ticket.status !== 'closed' && (
            <div className="border-t p-4">
              <form onSubmit={handleSendMessage} className="flex gap-2">
                <Textarea
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  placeholder={t('supportTicket.typeMessage')}
                  rows={2}
                  className="flex-1 resize-none"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleSendMessage(e);
                    }
                  }}
                />
                <Button
                  type="submit"
                  disabled={sending || !newMessage.trim()}
                  className="bg-[#be1f1a] hover:bg-[#a71b17] h-auto"
                >
                  <Send className="h-4 w-4" />
                </Button>
              </form>
              <p className="text-xs text-gray-500 mt-2">
                {t('supportTicket.sendHint')}
              </p>
            </div>
          )}

          {ticket.status === 'closed' && (
            <div className="border-t p-4 bg-gray-50 text-center">
              <p className="text-gray-600">{t('supportTicket.closed')}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SupportTicketChat;